// IE Hub - Budget Breakdown Field Configuration
// Single source of truth for budget categories, schema-to-API field mapping,
// and human-readable labels used in Budget Breakdown and related screens.

/**
 * Basic configuration shape for a single budget field.
 *
 * - `amount`: API field name for the amount value
 * - `frequency`: API field name for the frequency code (optional for assets)
 * - `notes`: API field name for the notes text (optional)
 * - `label`: Human-readable label for Budget Breakdown UI (optional for now;
 *            if omitted, callers can fall back to getFieldLabelByApiName)
 *
 * Additional keys (like supplier fields) are kept as generic strings so the
 * mapping utilities in `budgetFieldMapping.ts` can continue to use them.
 */
export interface BudgetFieldConfig {
  amount: string;
  frequency?: string;
  notes?: string;
  label?: string;
  // Allow additional API field mappings (e.g. supplier fields, flags)
  // without constraining the shape too tightly.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [extraKey: string]: any;
}

export type BudgetFieldCategoryConfig = Record<string, BudgetFieldConfig>;

export const BUDGET_FIELD_MAPPINGS = {
  // Income category mappings
  salary: {
    takeHomeSalary: {
      label: 'Take home salary',
      amount: 'basicIncome',
      frequency: 'basicIncomeMode',
      notes: 'basicIncomeNotes',
    },
    partnerTakeHomeSalary: {
      label: 'Partner take home salary',
      amount: 'debtor2BasicIncome',
      frequency: 'debtor2BasicIncomeMode',
      notes: 'debtor2BasicIncomeNotes',
    },
    otherSalarySource: {
      label: 'Other salary source',
      amount: 'otherSalaryWages1',
      frequency: 'otherSalaryWages1Mode',
      notes: 'otherSalaryWages1Notes',
    },
  },

  benefits: {
    universalCredit: {
      label: 'Universal credit',
      amount: 'otherIncludeUniversalCredit',
      frequency: 'otherIncludeUniversalCreditMode',
      notes: 'otherIncludeUniversalCreditNotes',
    },
    jobSeekerAllowanceContribute: {
      label: "Job seeker's allowance (contribution)",
      amount: 'jobseekerAllowanceContribute',
      frequency: 'jobseekerAllowanceContributeMode',
      notes: 'jobseekerAllowanceContributeNotes',
    },
    childBenefit: {
      label: 'Child benefit',
      amount: 'childBenefit',
      frequency: 'childBenefitMode',
      notes: 'childBenefitNotes',
    },
    empsupportallowancestatutory: {
      label: 'Employment and Support Allowance (contribution) or Statutory Sick Pay',
      amount: 'empSupportAllowanceStatutory',
      frequency: 'empSupportAllowanceStatutoryMode',
      notes: 'empSupportAllowanceStatutoryNotes',
    },
    disabilityAllowance: {
      label: 'Disability allowance',
      amount: 'dlaPipAttendanceAllowance',
      frequency: 'dalPipAttendanceAllowanceMode',
      // NOTE: API + IncomeNoteData use `dalPipAttendanceAllowanceNotes`
      // (with "dal" prefix). This was previously mis-typed as
      // `dlaPipAttendanceAllowanceNotes`, which caused notes payloads
      // to send an unknown field and could trigger 500s when saving
      // Disability Allowance notes from the Benefits screen.
      notes: 'dalPipAttendanceAllowanceNotes',
    },
    carerAllowance: {
      label: "Carer's allowance",
      amount: 'carerAllowance',
      frequency: 'carerAllowanceMode',
      notes: 'carerAllowanceNotes',
    },
    housingBenefitLocalAllowance: {
      label: 'Housing benefit',
      amount: 'housingBenefitLocalAllowance',
      frequency: 'housingBenefitLocalAllowanceMode',
      notes: 'housingBenefitLocalAllowanceNotes',
    },
    councilTaxSupport: {
      label: 'Council tax support',
      amount: 'councilTaxReduction',
      frequency: 'councilTaxReductionMode',
      notes: 'councilTaxReductionNotes',
    },
    studentLoansAndGrants: {
      label: 'Student loans and grants',
      amount: 'studentLoansAndGrants',
      frequency: 'studentLoansAndGrantsMode',
      notes: 'studentLoansAndGrantsNotes',
    },
    otherBenefits: {
      label: 'Other benefits',
      amount: 'otherBenefits',
      frequency: 'otherBenefitsMode',
      notes: 'otherBenefitsNotes',
    },
  },

  pension: {
    statePension: {
      label: 'State pension',
      amount: 'statePension',
      frequency: 'statePensionMode',
      notes: 'statePensionNotes',
    },
    privatePension: {
      label: 'Private pension',
      amount: 'privatePension',
      frequency: 'privatePensionMode',
      notes: 'privatePensionNotes',
    },
    pensionCredit: {
      label: 'Pension credit',
      amount: 'pensionCredit',
      frequency: 'pensionCreditMode',
      notes: 'pensionCreditNotes',
    },
    pensionOthers: {
      label: 'Other pension',
      amount: 'pensionOthers',
      frequency: 'pensionOthersMode',
      notes: 'pensionOthersNotes',
    },
  },

  otherSources: {
    childMaintenanceOrSupport: {
      label: 'Child maintenance or support',
      amount: 'maintenanceOrChildSupport',
      frequency: 'maintenanceOrChildSupportMode',
      notes: 'maintenanceOrChildSupportNotes',
    },
    bordersAndLodger: {
      label: 'Boarders and lodgers',
      amount: 'bordersAndLodger',
      frequency: 'bordersAndLodgerMode',
      notes: 'bordersAndLodgerNotes',
    },
    nonDependantContributions: {
      label: 'Non-dependant contributions',
      amount: 'nonDependantContributions',
      frequency: 'nonDependantContributionsMode',
      notes: 'nonDependantContributionsNotes',
    },
    moneyFromOtherSources: {
      label: 'Money from other sources',
      amount: 'otherIncome1',
      frequency: 'otherIncome1Mode',
      notes: 'otherIncome1Notes',
    },
  },

  // Asset category mappings (no frequency, just amount and notes)
  properties: {
    propertiesTotalValue: {
      label: 'Total value of properties',
      amount: 'totalValueProperties',
      notes: 'totalValuePropertiesNotes',
    },
    propertiesMortgageOutstanding: {
      label: 'Mortgage outstanding',
      amount: 'mortgageOutstanding',
      notes: 'mortgageOutstandingNotes',
    },
    propertiesSecuredLoanOutstanding: {
      label: 'Secured loan outstanding',
      amount: 'securedLoanOutstanding',
      notes: 'securedLoanOutstandingNotes',
    },
  },

  otherAssets: {
    valueOfVehicles: {
      label: 'Value of vehicles',
      amount: 'valueOfVehicles',
      notes: 'valueOfVehiclesNotes',
    },
    assetSavings: {
      label: 'Savings',
      amount: 'assetSavings',
      notes: 'assetSavingsNotes',
    },
    otherAssets: {
      label: 'Other assets',
      amount: 'otherAssets',
      notes: 'otherAssetsNotes',
    },
  },

  // Fixed expenses category mappings
  household: {
    houseRent: {
      label: 'Rent',
      amount: 'houseRent', // Or 'mortgage' depending on context
      frequency: 'houseRentMode',
      notes: 'houseRentNotes',
    },
    groundRentServiceCharge: {
      label: 'Ground rent/ service charge',
      amount: 'groundRentAndServiceCharges',
      frequency: 'groundRentAndServiceChargesMode',
      notes: 'groundRentAndServiceChargesNotes',
    },
    mortgage: {
      label: 'Mortgage',
      amount: 'mortgage',
      frequency: 'mortgageMode',
      notes: 'mortgageNotes',
    },
    securedLoans: {
      label: 'Secured loans',
      amount: 'otherSecuredLoans',
      frequency: 'otherSecuredLoansMode',
      notes: 'otherSecuredLoansNotes',
    },
    secondMortgage: {
      label: 'Endowment mortgage',
      amount: 'secondMortgage',
      frequency: 'secondMortgageMode',
      notes: 'secondMortgageNotes',
    },

    councilTax: {
      label: 'Council tax',
      amount: 'councilTax',
      frequency: 'councilTaxMode',
      notes: 'councilTaxNotes',
    },
    applianceFurnitureRental: {
      label: 'Appliance/ furniture rental',
      amount: 'otherRentalAppliances',
      frequency: 'otherRentalAppliancesMode',
      notes: 'otherRentalAppliancesNotes',
    },
    tvLicense: {
      label: 'TV license',
      amount: 'tvLicence',
      frequency: 'tvLicenceMode',
      notes: 'tvLicenceNotes',
    },
    otherHouseholdExpenses: {
      label: 'Other household expenses',
      amount: 'otherHomeCosts',
      frequency: 'otherHomeCostsMode',
      notes: 'otherHomeCostsNotes',
    },
  },

  utilities: {
    gas: {
      label: 'Gas',
      amount: 'gasBill', // Combined gas + electricity
      frequency: 'gasBillMode',
      notes: 'gasBillNotes',
      gasSupplier: 'gasSupplier',
      gasSupplierOther: 'gasSupplierOther',
      awarePriorityServices: 'awarePriorityServices',
      awareCarbonMonoxidePoison: 'awareCarbonMonoxidePoison',
    },
    electricity: {
      label: 'Electricity',
      amount: 'electricityBill',
      frequency: 'electricityBillMode',
      notes: 'electricityBillNotes',
      electricitySupplier: 'electricitySupplier',
      electricitySupplierOther: 'electricitySupplierOther',
    },
    water: {
      label: 'Water',
      amount: 'waterBill',
      frequency: 'waterBillMode',
      notes: 'waterBillNotes',
    },
    otherTypesFuel: {
      label: 'Other types of fuel',
      amount: 'otherUtilities',
      frequency: 'otherUtilitiesMode',
      notes: 'otherUtilitiesNotes',
    },
    waterWaste: {
      label: 'Water waste',
      amount: 'waterWaste',
      frequency: 'waterWasteMode',
      notes: 'waterWasteNotes',
    },
    otherUtilities: {
      label: 'Other utilities',
      amount: 'otherExpUtilities',
      frequency: 'otherExpUtilitiesMode',
      notes: 'otherExpUtilitiesNotes',
    },
  },

  care: {
    childcare: {
      label: 'Childcare',
      amount: 'childCare',
      frequency: 'childCareMode',
      notes: 'childCareNotes',
    },
    adultCare: {
      label: 'Adult care',
      amount: 'adultCareCosts',
      frequency: 'adultCareCostsMode',
      notes: 'adultCareCostsNotes',
    },
    childSupportMaintenance: {
      label: 'Child maintenance',
      amount: 'childMaintenance',
      frequency: 'childMaintenanceMode',
      notes: 'childMaintenanceNotes',
    },
    prescriptionsMedicine: {
      label: 'Prescriptions and medicine',
      amount: 'prescriptionAndMedicine',
      frequency: 'prescriptionAndMedicineMode',
      notes: 'prescriptionAndMedicineNotes',
    },
    dentalOptician: {
      label: 'Dental/ optician',
      amount: 'health',
      frequency: 'healthMode',
      notes: 'healthNotes',
    },
    otherHealthcare: {
      label: 'Other healthcare',
      amount: 'otherHealthCosts',
      frequency: 'otherHealthCostsMode',
      notes: 'otherHealthCostsNotes',
    },
  },

  transport: {
    publicTransport: {
      label: 'Public transport',
      amount: 'publicTransport',
      frequency: 'publicTransportMode',
      notes: 'publicTransportNotes',
    },
    vehicleCare: {
      label: 'Vehicle care (HP/lease)',
      amount: 'hirePurchase',
      frequency: 'hirePurchaseMode',
      notes: 'hirePurchaseNotes',
    },
    carInsurance: {
      label: 'Car insurance',
      amount: 'vehicleInsurance',
      frequency: 'vehicleInsuranceMode',
      notes: 'vehicleInsuranceNotes',
    },
    roadTax: {
      label: 'Road tax',
      amount: 'vehicleTax',
      frequency: 'vehicleTaxMode',
      notes: 'vehicleTaxNotes',
    },
    mot: {
      label: 'MOT/ servicing',
      amount: 'vehicleMaintenance',
      frequency: 'vehicleMaintenanceMode',
      notes: 'vehicleMaintenanceNotes',
    },
    breakdownCover: {
      label: 'Breakdown cover',
      amount: 'breakdownRecovery',
      frequency: 'breakdownRecoveryMode',
      notes: 'breakdownRecoveryNotes',
    },
    fuelParkingAndTollRoadCharges: {
      label: 'Fuel, parking and tolls',
      amount: 'vehicleFuel',
      frequency: 'vehicleFuelMode',
      notes: 'vehicleFuelNotes',
    },
    otherTransport: {
      label: 'Other transport costs',
      amount: 'otherCarCosts',
      frequency: 'otherCarCostsMode',
      notes: 'otherCarCostsNotes',
    },
  },

  school: {
    schoolUniforms: {
      label: 'School uniforms',
      amount: 'schoolUniform',
      frequency: 'schoolUniformMode',
      notes: 'schoolUniformNotes',
    },
    schoolClubsAndTrips: {
      label: 'School clubs and trips',
      amount: 'clubsAndSchoolTrips',
      frequency: 'clubsAndSchoolTripsMode',
      notes: 'clubsAndSchoolTripsNotes',
    },
    otherExpenses: {
      label: 'Other school expenses',
      amount: 'otherSchoolCosts',
      frequency: 'otherSchoolCostsMode',
      notes: 'otherSchoolCostsNotes',
    },
  },

  pensionInsurance: {
    pensionPay: {
      label: 'Pension payments',
      amount: 'pensionPayments',
      frequency: 'pensionPaymentsMode',
      notes: 'pensionPaymentsNotes',
    },
    lifeInsurance: {
      label: 'Life insurance',
      amount: 'lifeInsurance',
      frequency: 'lifeInsuranceMode',
      notes: 'lifeInsuranceNotes',
    },
    mortageAndProtectionInsurance: {
      label: 'Mortgage/ protection insurance',
      amount: 'protectionInsurance',
      frequency: 'protectionInsuranceMode',
      notes: 'protectionInsuranceNotes',
    },
    buildingContentsInsurance: {
      label: 'Building/ contents insurance',
      amount: 'buildingContentsInsurance',
      frequency: 'buildingContentsInsuranceMode',
      notes: 'buildingContentsInsuranceNotes',
    },
    healthInsurance: {
      label: 'Health insurance',
      amount: 'healthInsurance',
      frequency: 'healthInsuranceMode',
      notes: 'healthInsuranceNotes',
    },
    otherPension: {
      label: 'Other pension/ insurance costs',
      amount: 'otherPensionCosts',
      frequency: 'otherPensionCostsMode',
      notes: 'otherPensionCostsNotes',
    },
  },

  professionals: {
    professionalCourses: {
      label: 'Professional courses',
      amount: 'professionalCourses',
      frequency: 'professionalCoursesMode',
      notes: 'professionalCoursesNotes',
    },
    unionFees: {
      label: 'Union fees',
      amount: 'unionFees',
      frequency: 'unionFeesMode',
      notes: 'unionFeesNotes',
    },
    professionalFees: {
      label: 'Professional fees',
      amount: 'professionalFees',
      frequency: 'professionalFeesMode',
      notes: 'professionalFeesNotes',
    },
    otherProfessionalExpenses: {
      label: 'Other professional expenses',
      amount: 'otherProfessionalCosts',
      frequency: 'otherProfessionalCostsMode',
      notes: 'otherProfessionalCostsNotes',
    },
  },

  otherSourcesFixed: {
    otherFixedExpenses: {
      label: 'Other essential costs',
      amount: 'otherEssentialCosts',
      frequency: 'otherEssentialCostsMode',
      notes: 'otherEssentialCostsNotes',
    },
  },

  // Flexible expense mappings
  leisure: {
    landlineInternetTV: {
      label: 'Landline, internet and TV',
      amount: 'homePhone',
      frequency: 'homePhoneMode',
      notes: 'homePhoneNotes',
      landlineSupplier: 'landlineSupplier',
      landlineSupplierOther: 'landlineSupplierOther',
      broadbandSupplier: 'broadbandSupplier',
      broadbandSupplierOther: 'broadbandSupplierOther',
    },
    mobilePhones: {
      label: 'Mobile phones',
      amount: 'mobile',
      frequency: 'mobileMode',
      notes: 'mobileNotes',
      mobileSupplier: 'mobileSupplier',
      mobileSupplierOther: 'mobileSupplierOther',
    },
    hobbiesLeisureSports: {
      label: 'Hobbies, leisure and sports',
      amount: 'hobbiesLeisureAndSport',
      frequency: 'hobbiesLeisureAndSportMode',
      notes: 'hobbiesLeisureAndSportNotes',
    },
    // Nested question-group inside hobbiesLeisureSports for gambling-specific spend
    gamblingFromHobbiesLeisure: {
      label: 'Gambling spending',
      amount: 'gamblingFromHobbiesLeisure',
      frequency: 'gamblingFromHobbiesLeisureMode',
      // No dedicated notes field on backend for gambling; parent notes used
    },

    gifts: {
      label: 'Gifts',
      amount: 'gifts',
      frequency: 'giftsMode',
      notes: 'giftsNotes',
    },
    pocketMoney: {
      label: 'Pocket money and school trips',
      amount: 'pocketMoneyAndSchoolTrips',
      frequency: 'pocketMoneyAndSchoolTripsMode',
      notes: 'pocketMoneyAndSchoolTripsNotes',
    },
    newspapersMagazines: {
      label: 'Newspapers and magazines',
      amount: 'newspapersAndMagazines',
      frequency: 'newspapersAndMagazinesMode',
      notes: 'newspapersAndMagazinesNotes',
    },
    otherLeisure: {
      label: 'Other communication costs',
      amount: 'otherCommunicationCosts',
      frequency: 'otherCommunicationCostsMode',
      notes: 'otherCommunicationCostsNotes',
    },
  },

  housekeeping: {
    groceries: {
      label: 'Food and groceries',
      amount: 'foodAndGroceries',
      frequency: 'foodAndGroceriesMode',
      notes: 'foodAndGroceriesNotes',
    },
    nappiesAndBabyItems: {
      label: 'Nappies and baby items',
      amount: 'nappiesAndBabyItems',
      frequency: 'nappiesAndBabyItemsMode',
      notes: 'nappiesAndBabyItemsNotes',
    },
    workSchoolMeal: {
      label: 'Work/ school meals',
      amount: 'meals',
      frequency: 'mealsMode',
      notes: 'mealsNotes',
    },
    laundry: {
      label: 'Laundry',
      amount: 'laundryBill',
      frequency: 'laundryBillMode',
      notes: 'laundryBillNotes',
    },
    alcohol: {
      label: 'Alcohol',
      amount: 'beerAndAlcohol',
      frequency: 'beerAndAlcoholMode',
      notes: 'beerAndAlcoholNotes',
    },
    smoking: {
      label: 'Smoking',
      amount: 'cigarettesAndTobacco',
      frequency: 'cigarettesAndTobaccoMode',
      notes: 'cigarettesAndTobaccoNotes',
    },
    pet: {
      label: 'Pet costs',
      amount: 'vetBillsAndPetInsurance',
      frequency: 'vetBillsAndPetInsuranceMode',
      notes: 'vetBillsAndPetInsuranceNotes',
    },
    houseRepair: {
      label: 'Home repairs',
      amount: 'homeRepairs',
      frequency: 'homeRepairsMode',
      notes: 'homeRepairsNotes',
    },
    otherHousekeeping: {
      label: 'Other housekeeping costs',
      amount: 'housekeepingOthers',
      frequency: 'housekeepingOthersMode',
      notes: 'housekeepingOthersNotes',
    },
  },

  personalCosts: {
    clothingFootwear: {
      label: 'Clothing and footwear',
      amount: 'clothing',
      frequency: 'clothingMode',
      notes: 'clothingNotes',
    },
    hairdressing: {
      label: 'Hairdressing',
      amount: 'hairdressing',
      frequency: 'hairdressingMode',
      notes: 'hairdressingNotes',
    },
    toiletries: {
      label: 'Cleaning and toiletries',
      amount: 'cleaningToileteries',
      frequency: 'cleaningToileteriesMode',
      notes: 'cleaningToileteriesNotes',
    },
    otherPersonalCare: {
      label: 'Other personal care',
      amount: 'otherPersonalCosts',
      frequency: 'otherPersonalCostsMode',
      notes: 'otherPersonalCostsNotes',
    },
  },

  savings: {
    savingsAmount: {
      label: 'Monthly savings amount',
      amount: 'monthlySavingAmount',
      notes: 'monthlySavingAmountNotes',
    },
  },

  // Debt categories - these are handled separately via DebtCategory component
  debtsAdministration: {
    // Administration fields for debts section
    administrationFee: {
      label: 'Debt administration fee',
      amount: 'debtAdminFee',
      frequency: 'debtAdminFeeMode',
      notes: 'debtAdminFeeNotes',
    },
  },

  debts: {
    // Debt fields are managed through the DebtCategory component
    // which stores arrays of debt items
  },
} as const;


